import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError } from "../../../utils/showApiErrorToast";
import {
  Box,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import {
  fetchManagementExplorer,
  fetchManagementExplorerSliceDetail,
} from "../../../services/reportsApi";
import type {
  ManagementExplorerQuery,
  ManagementExplorerSliceRow,
  ManagementExplorerSort,
} from "../../../types/managementExplorer";
import {
  buildExitPlacementPick,
  findExitPlacementPickForSlice,
  pickedExitPlacementSliceIds,
} from "../../../utils/exitPlacementPicks";
import {
  addPick,
  readPickListForReport,
  removePick,
  REPORT_PICK_LIST_CHANGED_EVENT,
} from "../../../utils/reportPickListStorage";
import { humanizeManagementActions } from "../../../utils/managementActionLabels";
import {
  exitPlacementSliceDisplayLabel,
  formatPriceDistanceFromEntry,
  levelSourceCaveat,
  sliceCanAddToCart,
  sliceExitDisplay,
  sliceHasActionableExit,
  sliceHasStopOrTargetPrice,
  sliceNoActionMessage,
} from "../../../utils/exitPlacementDisplay";
import {
  buildExplorerSliceId,
  explorerListFromSnapshot,
  extractManagementExplorerFromSnapshot,
  normalizeExplorerSlice,
} from "../../../utils/managementExplorer";
import { EXIT_PLACEMENT_SIM_PRODUCT_COPY } from "../../../utils/reportTypeRules";
import { regimeDrilldownTableKeyFromBreakdownMap } from "../../../utils/marketContextDrilldownLabels";
import { reportDetailSectionCardProps } from "./components/ReportsCardChrome";
import {
  REPORTS_DETAIL_FIELD_DESC_CLASS,
  REPORTS_DETAIL_MUTED_TEXT_CLASS,
} from "./reportsDetailTheme";
import { ExitPlacementExplorerTable } from "./components/ExitPlacementExplorerTable";
import { getExplorerCategoryTabLabel } from "./marketContextSliceDisplay";

const FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const DETAIL_BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed text-gray-700 dark:text-sidebar-foreground";

const DETAIL_MUTED_CLASS = `font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed ${REPORTS_DETAIL_MUTED_TEXT_CLASS}`;

const SORT_OPTIONS: { value: ManagementExplorerSort; label: string }[] = [
  { value: "expectancy_asc", label: "Expectancy (low first)" },
  { value: "pnl_desc", label: "PnL (high first)" },
  { value: "actionable_first", label: "Actionable first" },
];

function explorerRowMatchesFocusId(row: ManagementExplorerSliceRow, target: string): boolean {
  const id = buildExplorerSliceId(row);
  return id === target || String(row.slice_id ?? "").trim() === target;
}

export type ManagementExplorerSectionProps = {
  reportId: string;
  inputSnapshot: unknown;
  textColor: string;
  subtleText: string;
  /** Report candle TF — scales long/short direction bar-window labels in category badges. */
  candleTimeframe?: string | null;
  /** When set (e.g. from overview evidence), highlight that slice in the explorer table once. */
  focusSliceId?: string | null;
  /** Bumped when the user re-opens the same slice from overview evidence. */
  focusNonce?: number;
  /** Called after the one-time highlight is applied so parent state can clear. */
  onDismissExplorerFocus?: () => void;
  onPreviewExplorerSlice?: (row: ManagementExplorerSliceRow) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
};

function ExplorerSliceDetailPanel(props: {
  row: ManagementExplorerSliceRow;
  detail: ManagementExplorerSliceRow | null;
  detailLoading: boolean;
}): React.ReactElement {
  const row = props.detail ?? props.row;
  const display = sliceExitDisplay(row);
  const exit = row.exit_placement;
  const rationale = (exit?.rationale ?? []).filter((l) => String(l ?? "").trim());
  const suppressed = exit?.suppressed ?? {};
  const suppressedEntries = Object.entries(suppressed).filter(([, v]) => String(v ?? "").trim());
  const levelCaveat = levelSourceCaveat(exit?.level_source);
  const hasStopOrTarget = sliceHasStopOrTargetPrice(row);
  const noActionMessage = sliceNoActionMessage(row);
  const showNoActionInfo = !sliceHasActionableExit(row) || !hasStopOrTarget;
  const ruleActions = row.simulation_rule?.actions;
  const actionLines = humanizeManagementActions(
    Array.isArray(ruleActions) ? ruleActions : undefined,
    {
      baselineSlDistanceAtr: row.exit_placement?.engine?.sl_distance_atr,
      baselineTpDistanceAtr: row.exit_placement?.engine?.tp_distance_atr,
    }
  );

  return (
    <div
      data-exit-placement-detail-body=""
      className="flex flex-col gap-4 font-[family-name:var(--font-primary)]"
    >
      {props.detailLoading ? (
        <div className={`flex items-center gap-2 ${DETAIL_MUTED_CLASS}`}>
          <Loader2 className="size-4 shrink-0 animate-spin text-[color:var(--brand-tropical-surf)]" />
          <span>Loading slice detail…</span>
        </div>
      ) : null}

      {row.excursion_card?.headline || row.excursion_card?.subheadline ? (
        <Field className="gap-1.5">
          <FieldLabel className={FIELD_LABEL_CLASS}>Excursion</FieldLabel>
          {row.excursion_card?.headline ? (
            <p className={`m-0 ${DETAIL_BODY_CLASS}`}>{row.excursion_card.headline}</p>
          ) : null}
          {row.excursion_card?.subheadline ? (
            <FieldDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
              {row.excursion_card.subheadline}
            </FieldDescription>
          ) : null}
        </Field>
      ) : null}

      {display && hasStopOrTarget ? (
        <Field className="gap-1.5">
          <FieldLabel className={FIELD_LABEL_CLASS}>Stop &amp; target (Option A)</FieldLabel>
          <div className="flex flex-col gap-1">
            <p className={`m-0 ${DETAIL_BODY_CLASS}`}>
              Stop: {formatPriceDistanceFromEntry(display.sl_distance_price, display.symbol)}
            </p>
            <p className={`m-0 ${DETAIL_BODY_CLASS}`}>
              Target: {formatPriceDistanceFromEntry(display.tp_distance_price, display.symbol)}
            </p>
          </div>
        </Field>
      ) : null}

      {showNoActionInfo && noActionMessage ? (
        <div className="rounded-[6px] border border-border bg-muted/40 p-3 dark:bg-sidebar">
          <Field className="gap-1.5">
            <FieldLabel className={FIELD_LABEL_CLASS}>No SL/TP recommendation</FieldLabel>
            <p className={`m-0 ${DETAIL_BODY_CLASS}`}>{noActionMessage}</p>
          </Field>
        </div>
      ) : null}

      {rationale.length ? (
        <Field className="gap-1.5">
          <FieldLabel className={FIELD_LABEL_CLASS}>Why</FieldLabel>
          <ul className={`m-0 list-disc space-y-1 ps-4 ${DETAIL_BODY_CLASS}`}>
            {rationale.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Field>
      ) : null}

      {suppressedEntries.length ? (
        <Field className="gap-1.5">
          <FieldLabel className={FIELD_LABEL_CLASS}>Suppressed</FieldLabel>
          <ul className={`m-0 list-none space-y-1 p-0 ${DETAIL_MUTED_CLASS}`}>
            {suppressedEntries.map(([k, v]) => (
              <li key={k}>{v}</li>
            ))}
          </ul>
        </Field>
      ) : null}

      {actionLines.length ? (
        <Field className="gap-1.5">
          <FieldLabel className={FIELD_LABEL_CLASS}>Suggested actions</FieldLabel>
          <ul className={`m-0 list-disc space-y-1 ps-4 ${DETAIL_BODY_CLASS}`}>
            {actionLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Field>
      ) : null}

      <Field className="gap-1.5">
        <FieldLabel className={FIELD_LABEL_CLASS}>Confidence</FieldLabel>
        <p className={`m-0 ${DETAIL_MUTED_CLASS}`}>
          Sample: {row.sample_size ?? "—"}
          {exit?.level_source ? ` · Source: ${exit.level_source}` : ""}
        </p>
        {levelCaveat ? (
          <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed text-[color:var(--brand-comparison-period)]">
            {levelCaveat}
          </p>
        ) : null}
      </Field>
    </div>
  );
}

export function ManagementExplorerSection(props: ManagementExplorerSectionProps): React.ReactElement {
  const sectionRef = useRef<HTMLDivElement>(null);
  const lastAppliedFocusKeyRef = useRef("");
  const { explorer, slices: snapshotSlices } = useMemo(
    () => extractManagementExplorerFromSnapshot(props.inputSnapshot),
    [props.inputSnapshot]
  );
  const snapshotAllSlices = useMemo(
    () => snapshotSlices.map(normalizeExplorerSlice),
    [snapshotSlices]
  );

  const [sort, setSort] = useState<ManagementExplorerSort>("expectancy_asc");
  const [losingOnly, setLosingOnly] = useState(true);
  const [actionableExitOnly, setActionableExitOnly] = useState(true);
  const [categoryTab, setCategoryTab] = useState("all");

  const [slices, setSlices] = useState<ManagementExplorerSliceRow[]>(() =>
    explorerListFromSnapshot(props.inputSnapshot, {
      sort: "expectancy_asc",
      losing_only: true,
      actionable_exit_only: true,
    }).slices
  );
  const [loading, setLoading] = useState(false);
  const [detailDialogId, setDetailDialogId] = useState<string | null>(null);
  const [detailById, setDetailById] = useState<Record<string, ManagementExplorerSliceRow>>({});
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [savedPicks, setSavedPicks] = useState(() => readPickListForReport(props.reportId));
  const focusNonce = props.focusNonce ?? 0;
  const [focusPulseNonce, setFocusPulseNonce] = useState(0);
  const [evidenceHighlightRowId, setEvidenceHighlightRowId] = useState<string | undefined>();
  const [pendingEvidenceFocusId, setPendingEvidenceFocusId] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => setSavedPicks(readPickListForReport(props.reportId));
    sync();
    window.addEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as EventListener);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(REPORT_PICK_LIST_CHANGED_EVENT, sync as EventListener);
      window.removeEventListener("storage", sync);
    };
  }, [props.reportId]);

  const scrollExplorerSectionIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, []);

  const pickedSliceIds = useMemo(
    () => pickedExitPlacementSliceIds(savedPicks),
    [savedPicks]
  );

  const categoryTabOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of slices) {
      const breakdownMap = String(row.breakdown_map ?? "").trim();
      if (!breakdownMap) continue;
      const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
      if (!tableKey) continue;
      counts.set(tableKey, (counts.get(tableKey) ?? 0) + 1);
    }
    const ranked = Array.from(counts.entries()).sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
    );
    return [
      { key: "all", label: "All", count: slices.length },
      ...ranked.map(([key, count]) => ({
        key,
        label: getExplorerCategoryTabLabel(key, props.candleTimeframe),
        count,
      })),
    ];
  }, [props.candleTimeframe, slices]);

  const filteredSlices = useMemo(() => {
    if (categoryTab === "all") return slices;
    return slices.filter((row) => {
      const breakdownMap = String(row.breakdown_map ?? "").trim();
      return regimeDrilldownTableKeyFromBreakdownMap(breakdownMap) === categoryTab;
    });
  }, [categoryTab, slices]);

  useEffect(() => {
    if (categoryTab === "all") return;
    if (categoryTabOptions.some((option) => option.key === categoryTab)) return;
    setCategoryTab("all");
  }, [categoryTab, categoryTabOptions]);

  const query = useMemo(
    (): ManagementExplorerQuery => ({
      losing_only: losingOnly || undefined,
      actionable_exit_only: actionableExitOnly || undefined,
      sort,
    }),
    [actionableExitOnly, losingOnly, sort]
  );

  const reload = useCallback(async () => {
    if (!props.reportId) return;
    setLoading(true);
    try {
      const data = await fetchManagementExplorer(props.reportId, query);
      setSlices(data.slices.map(normalizeExplorerSlice));
    } catch {
      const local = explorerListFromSnapshot(props.inputSnapshot, query);
      setSlices(local.slices);
    } finally {
      setLoading(false);
    }
  }, [props.inputSnapshot, props.reportId, query]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const sliceById = useMemo(() => {
    const m = new Map<string, ManagementExplorerSliceRow>();
    for (const row of slices) {
      const id = buildExplorerSliceId(row);
      if (id) m.set(id, row);
    }
    return m;
  }, [slices]);

  const toggleRowPick = useCallback(
    (row: ManagementExplorerSliceRow) => {
      const sliceId = buildExplorerSliceId(row);
      if (!sliceId) return;

      const existing = findExitPlacementPickForSlice(savedPicks, sliceId);
      if (existing) {
        removePick(props.reportId, existing.id);
        toast.success("Removed from picks", { description: existing.label });
        return;
      }

      if (!sliceCanAddToCart(row) || !buildExplorerSliceId(row)) {
        toastError("Cannot add pick", {
          description: "This context has no executable SL/TP rule.",
        });
        return;
      }

      const pick = buildExitPlacementPick(row, props.reportId, "single");
      if (!pick) {
        toastError("Cannot add pick", {
          description: "This context has no executable SL/TP rule.",
        });
        return;
      }
      const res = addPick(pick);
      if (res.ok) {
        toast.success("Added to picks", {
          description: `${pick.label} · Available Single Picks`,
        });
      } else {
        toast.info("Pick already added", { description: res.reason });
      }
    },
    [props.reportId, savedPicks]
  );

  const fetchSliceDetail = useCallback(
    async (row: ManagementExplorerSliceRow, id: string) => {
      if (detailById[id]) return;
      setDetailLoadingId(id);
      try {
        const resp = await fetchManagementExplorerSliceDetail(props.reportId, id);
        const slice = resp.slice ? normalizeExplorerSlice(resp.slice) : row;
        setDetailById((prev) => ({ ...prev, [id]: slice }));
      } catch {
        setDetailById((prev) => ({ ...prev, [id]: row }));
      } finally {
        setDetailLoadingId(null);
      }
    },
    [detailById, props.reportId]
  );

  const openDetails = useCallback(
    (row: ManagementExplorerSliceRow) => {
      const id = buildExplorerSliceId(row);
      if (!id) return;
      setDetailDialogId(id);
      void fetchSliceDetail(row, id);
    },
    [fetchSliceDetail]
  );

  useEffect(() => {
    const target = props.focusSliceId?.trim();
    if (!target || focusNonce <= 0) return;

    const focusKey = `${target}:${focusNonce}`;
    if (lastAppliedFocusKeyRef.current === focusKey) return;

    const row =
      snapshotAllSlices.find((candidate) => explorerRowMatchesFocusId(candidate, target)) ??
      slices.find((candidate) => explorerRowMatchesFocusId(candidate, target));
    if (!row) return;

    lastAppliedFocusKeyRef.current = focusKey;

    const breakdownMap = String(row.breakdown_map ?? "").trim();
    const tableKey = regimeDrilldownTableKeyFromBreakdownMap(breakdownMap);
    const categoryTabForRow =
      tableKey && categoryTabOptions.some((option) => option.key === tableKey) ? tableKey : "all";

    const visibleInCurrentView = filteredSlices.some((candidate) =>
      explorerRowMatchesFocusId(candidate, target)
    );
    const visibleInCategoryAll = slices.some((candidate) =>
      explorerRowMatchesFocusId(candidate, target)
    );

    if (visibleInCurrentView) {
      setPendingEvidenceFocusId(target);
      scrollExplorerSectionIntoView();
      const label = exitPlacementSliceDisplayLabel(row);
      toast.success("Opened in Exit Placement Explorer", {
        description: label || target,
      });
      return;
    }

    if (visibleInCategoryAll && categoryTab !== categoryTabForRow) {
      setCategoryTab(categoryTabForRow);
      setPendingEvidenceFocusId(target);
      scrollExplorerSectionIntoView();
      const label = exitPlacementSliceDisplayLabel(row);
      toast.success("Opened in Exit Placement Explorer", {
        description: label || target,
      });
      return;
    }

    scrollExplorerSectionIntoView();
    const label = exitPlacementSliceDisplayLabel(row);
    const filterHints: string[] = [];
    if (losingOnly) filterHints.push("Losing contexts only");
    if (actionableExitOnly) filterHints.push("Actionable SL/TP only");
    toast.info("Segment hidden by filters", {
      description:
        filterHints.length > 0
          ? `${label || target} is not in the current view (${filterHints.join(" · ")}). Adjust filters to see it.`
          : `${label || target} is not in the current explorer view. Adjust filters to see it.`,
    });
  }, [
    props.focusSliceId,
    actionableExitOnly,
    categoryTab,
    categoryTabOptions,
    filteredSlices,
    focusNonce,
    losingOnly,
    scrollExplorerSectionIntoView,
    slices,
    snapshotAllSlices,
  ]);

  useEffect(() => {
    if (!pendingEvidenceFocusId) return;

    const rowVisible = filteredSlices.some((candidate) =>
      explorerRowMatchesFocusId(candidate, pendingEvidenceFocusId)
    );
    if (!rowVisible) return;

    setEvidenceHighlightRowId(pendingEvidenceFocusId);
    setFocusPulseNonce((n) => n + 1);
    setPendingEvidenceFocusId(null);
    props.onDismissExplorerFocus?.();
  }, [filteredSlices, pendingEvidenceFocusId, props.onDismissExplorerFocus]);

  useEffect(() => {
    if (!evidenceHighlightRowId || focusPulseNonce <= 0) return;
    const timer = window.setTimeout(() => {
      setEvidenceHighlightRowId(undefined);
      setFocusPulseNonce(0);
    }, 3400);
    return () => window.clearTimeout(timer);
  }, [evidenceHighlightRowId, focusPulseNonce]);

  const detailDialogRow = useMemo(() => {
    if (!detailDialogId) return null;
    return (
      sliceById.get(detailDialogId) ??
      slices.find((r) => {
        const id = buildExplorerSliceId(r);
        return id === detailDialogId || String(r.slice_id ?? "").trim() === detailDialogId;
      }) ??
      null
    );
  }, [detailDialogId, sliceById, slices]);

  const detailDialogTitle = detailDialogRow
    ? exitPlacementSliceDisplayLabel(detailDialogRow) || "Context details"
    : "Context details";

  if (!explorer && snapshotSlices.length === 0) {
    return (
      <Card {...reportDetailSectionCardProps} data-exit-placement-explorer-card="">
        <CardBody p={6}>
          <Text fontSize="sm" color={props.subtleText} fontFamily="var(--font-primary)">
            This report does not include a management explorer snapshot. Regenerate the Exit Placement
            report to browse contexts and load SL/TP policies into the simulator.
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card {...reportDetailSectionCardProps} data-exit-placement-explorer-card="">
      <CardBody display="flex" flexDirection="column" pt={{ base: 4, md: 5 }} w="full">
    <VStack ref={sectionRef} align="stretch" spacing={4} data-management-explorer="" data-exit-placement-explorer="">
      <Box>
        <Text fontSize={{ base: "xl", md: "2xl" }} color="#38BBD1" fontFamily="var(--font-primary)" mb={1}>
          Exit Placement Explorer
        </Text>
        <Text fontSize="sm" color={props.subtleText} className={REPORTS_DETAIL_FIELD_DESC_CLASS} lineHeight="1.5">
          {EXIT_PLACEMENT_SIM_PRODUCT_COPY} Use <strong>Details</strong> for full excursion and SL/TP
          rationale. Use <strong>+</strong> to add to simulator picks.
        </Text>
      </Box>

      <Flex gap={3} flexWrap="wrap" align="flex-end">
        <Flex gap={3} flex="0 1 auto" flexWrap="nowrap" align="flex-end" minW={0}>
          <Box flex="0 0 auto">
            <Text fontSize="xs" mb={1} className={FIELD_LABEL_CLASS}>
              Sort
            </Text>
            <FieldSelectFit
              filterChrome="reports"
              value={sort}
              onValueChange={(v) => setSort(v as ManagementExplorerSort)}
              options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Box>
          {categoryTabOptions.length > 2 ? (
            <Box flex="0 0 auto">
              <Text fontSize="xs" mb={1} className={FIELD_LABEL_CLASS}>
                Category
              </Text>
              <FieldSelectFit
                filterChrome="reports"
                value={categoryTab}
                onValueChange={setCategoryTab}
                options={categoryTabOptions.map((o) => ({
                  value: o.key,
                  label: `${o.label} (${o.count})`,
                }))}
              />
            </Box>
          ) : null}
        </Flex>
        <Flex gap={4} flexWrap="wrap" align="center" flex="0 0 auto" pb={1}>
          <Flex align="center" gap={2}>
            <Checkbox
              id="explorer-losing-only"
              data-trade-log-table-checkbox=""
              checked={losingOnly}
              onCheckedChange={(v) => setLosingOnly(v === true)}
              disabled={loading}
            />
            <label
              htmlFor="explorer-losing-only"
              className="text-sm text-gray-500 dark:text-sidebar-foreground"
            >
              Losing contexts only
            </label>
          </Flex>
          <Flex align="center" gap={2}>
            <Checkbox
              id="explorer-actionable-exit-only"
              data-trade-log-table-checkbox=""
              checked={actionableExitOnly}
              onCheckedChange={(v) => setActionableExitOnly(v === true)}
              disabled={loading}
            />
            <label
              htmlFor="explorer-actionable-exit-only"
              className="text-sm text-gray-500 dark:text-sidebar-foreground"
            >
              Actionable SL/TP only
            </label>
          </Flex>
        </Flex>
      </Flex>

      <Text fontSize="xs" color={props.subtleText} lineHeight="1.5">
        Column headers sort all loaded rows for the current filters. The Sort dropdown above
        re-ranks from the server (e.g. expectancy vs PnL).
      </Text>

      <ExitPlacementExplorerTable
        key={`${categoryTab}-${sort}-${losingOnly ? 1 : 0}-${actionableExitOnly ? 1 : 0}`}
        rows={filteredSlices}
        pickedSliceIds={pickedSliceIds}
        onTogglePick={toggleRowPick}
        onOpenDetails={openDetails}
        onPreviewSlice={props.onPreviewExplorerSlice}
        previewActiveKey={props.previewActiveKey}
        previewLoadingKey={props.previewLoadingKey}
        activeRowId={evidenceHighlightRowId}
        evidencePulseNonce={focusPulseNonce > 0 ? focusPulseNonce : undefined}
        candleTimeframe={props.candleTimeframe}
      />

      <Dialog
        open={Boolean(detailDialogId && detailDialogRow)}
        onOpenChange={(open) => {
          if (!open) setDetailDialogId(null);
        }}
      >
        <DialogContent
          data-exit-placement-detail-dialog=""
          showCloseButton={false}
          className="flex max-h-[min(90vh,40rem)] flex-col gap-4 sm:max-w-lg"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
              {detailDialogTitle}
            </DialogTitle>
            <DialogDescription className={REPORTS_DETAIL_FIELD_DESC_CLASS}>
              Excursion, stop/target levels, and rationale for this context.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto pe-1">
            {detailDialogRow ? (
              <ExplorerSliceDetailPanel
                row={detailDialogRow}
                detail={
                  detailDialogId && detailById[detailDialogId]
                    ? detailById[detailDialogId]
                    : null
                }
                detailLoading={detailLoadingId === detailDialogId}
              />
            ) : null}
          </div>

          <DialogFooter className="shrink-0 sm:justify-end">
            <DialogClose asChild>
              <Button
                type="button"
                variant="gradient"
                data-font="primary"
                data-exit-placement-detail-close=""
              >
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </VStack>
      </CardBody>
    </Card>
  );
}
